import React from "react"
import {
  withPropsOnChange,
  compose,
  withStateHandlers,
  withHandlers,
  mapProps,
} from "recompact"
import { connect } from "react-redux"
import get from "lodash/get"
import noop from "lodash/noop"
import identity from "lodash/identity"
import moment from "moment"
import juxt from "ramda/es/juxt"
import pipe from "ramda/es/pipe"
import GlobalEscapeHandle from "src/components/GlobalEscapeHandle"
import GlobalMouseHandle from "src/components/GlobalMouseHandle"
import entityApi from "src/hocs/entity"
import eventModel from "src/models/event"
import { requestValidate } from "src/engine/modules/validate/actions/creators"
import makeCompareFunction from "src/helpers/makeCompareFunction"
import {
  snapToGrid,
  limitDate,
  getTimeLabel,
  getSnapUnit,
  getGreaterSnapUnit,
  snapUnits,
} from "../helpers/withItemHelpers"
import { EVENT_ABSOLUTE_MIN_DURATION } from "../constants"

const LEFT = `LEFT`
const RIGHT = `RIGHT`

// Stored outside the component's state to prevent excessive renders
let cursorTime

// Adds the ability to resize existing items by stretching them
export default compose(
  connect(null, {
    requestValidate,
  }),

  mapProps(props => ({
    outerProps: props,
  })),

  withStateHandlers(
    {
      activeEventId: null,
      activeEvent: null,
      startDate: null,
      endDate: null,
      isGrabbing: false,
      grabHandle: null,
      isDragging: false,
      isPreDragging: false,
      dragOffset: null,
      dragEventDuration: null,
      dragInitialDate: null,
      preDragTimeOut: null,
    },
    {
      setState: () => identity,
    },
  ),

  entityApi(({ activeEventId }) => ({
    to: `event`,
    model: eventModel,
    id: activeEventId,
    restrictFetch: true,
  })),

  // Call a callback when pending ends
  withPropsOnChange(
    (props, nextProps) =>
      props.event &&
      nextProps.event &&
      !nextProps.event.pending &&
      nextProps.event.pending !== props.event.pending,
    ({
      activeEventId,
      event,
      outerProps: { onEventEdited, planId, requestValidate },
    }) => {
      if (activeEventId && event.data) {
        onEventEdited(event.data)
      }
      if (planId && requestValidate) {
        requestValidate({ plan: planId })
      }
    },
  ),

  withHandlers({
    handleMouseUp: ({
      setState,
      isGrabbing,
      isDragging,
      activeEvent,
      startDate,
      endDate,
      preDragTimeOut,
      event: {
        api: { update },
      },
    }) => () => {
      if (isGrabbing || isDragging) {
        update({
          ...activeEvent,
          startDateTime: startDate,
          endDateTime: endDate,
          // Не передаем значения поля, оно останется тем же
          resourceUuids: null,
        })
      }
      setState({
        isGrabbing: false,
        isDragging: false,
        isPreDragging: false,
        preDragTimeOut: clearTimeout(preDragTimeOut),
      })
    },
    handleEscapePress: ({ setState, preDragTimeOut }) => () =>
      setState({
        isGrabbing: false,
        isDragging: false,
        isPreDragging: false,
        activeEventId: null,
        activeEvent: null,
        preDragTimeOut: clearTimeout(preDragTimeOut),
      }),
    handleLeftHandlerMouseDown: ({ setState }) => (
      activeEventId,
      activeEvent,
      startDate,
      endDate,
    ) =>
      setState({
        startDate,
        endDate,
        activeEventId,
        activeEvent,
        isGrabbing: true,
        grabHandle: LEFT,
      }),
    handleRightHandlerMouseDown: ({ setState }) => (
      activeEventId,
      activeEvent,
      startDate,
      endDate,
    ) =>
      setState({
        startDate,
        endDate,
        activeEventId,
        activeEvent,
        isGrabbing: true,
        grabHandle: RIGHT,
      }),
    handleItemMouseDown: ({ setState }) => (
      activeEventId,
      activeEvent,
      startDate,
      endDate,
    ) =>
      setState({
        startDate,
        endDate,
        activeEventId,
        activeEvent,
        isPreDragging: true,
        dragEventDuration: endDate.diff(startDate),
        dragOffset: moment(cursorTime).diff(startDate),
        dragInitialDate: moment(cursorTime),
        preDragTimeOut: setTimeout(
          () =>
            setState({
              isDragging: true,
              isPreDragging: false,
            }),
          300,
        ),
      }),
    handleCanvasMouseMove: ({
      setState,
      grabHandle,
      startDate,
      endDate,
      activeEventId,
      activeEvent,
      isGrabbing,
      isDragging,
      dragOffset,
      dragEventDuration,
      isPreDragging,
      dragInitialDate,
      preDragTimeOut,
      outerProps: {
        items,
        plan,
        onCanvasMouseMove = noop,
        currentScale,
        eventTypes,
      },
    }) =>
      juxt([
        onCanvasMouseMove,
        // Resizing logic
        timestamp =>
          isGrabbing &&
          setState({
            [grabHandle === LEFT ? `startDate` : `endDate`]: pipe(
              // Momentify
              () => moment(timestamp),
              // Snap to grid
              date =>
                snapToGrid(
                  date,
                  activeEvent.eventTypeAllowOnlyDate
                    ? getGreaterSnapUnit(
                        getSnapUnit(currentScale),
                        snapUnits.day,
                      )
                    : getSnapUnit(currentScale),
                ),
              // Limiting date
              date => {
                const leftHandle = grabHandle === LEFT
                const rightHandle = grabHandle === RIGHT
                const { minExecutionTime, maxExecutionTime } =
                  eventTypes.find(
                    one =>
                      one.id ===
                      items.find(item => item.originId === activeEventId)
                        .typeId,
                  ) || {}

                const findMin = () => {
                  if (rightHandle) {
                    const limits = [
                      startDate
                        .clone()
                        .add(EVENT_ABSOLUTE_MIN_DURATION, `milliseconds`),
                      minExecutionTime
                        ? startDate.clone().add(minExecutionTime, `seconds`)
                        : null,
                    ].filter(Boolean)

                    return moment.max(limits)
                  }

                  if (leftHandle) {
                    const limits = [
                      plan ? moment(get(plan, `data.startDateTime`)) : null,
                      maxExecutionTime
                        ? endDate.clone().subtract(maxExecutionTime, `seconds`)
                        : null,
                    ].filter(Boolean)

                    return limits.length ? moment.max(limits) : null
                  }
                  return null
                }

                const findMax = () => {
                  if (rightHandle) {
                    const limits = [
                      plan ? moment(get(plan, `data.endDateTime`)) : null,
                      maxExecutionTime
                        ? startDate.clone().add(maxExecutionTime, `seconds`)
                        : null,
                    ].filter(Boolean)

                    return limits.length ? moment.min(limits) : null
                  }

                  if (leftHandle) {
                    const limits = [
                      endDate
                        .clone()
                        .subtract(EVENT_ABSOLUTE_MIN_DURATION, `milliseconds`),
                      minExecutionTime
                        ? endDate.clone().subtract(minExecutionTime, `seconds`)
                        : null,
                    ].filter(Boolean)

                    return moment.min(limits)
                  }
                  return null
                }

                return limitDate(date, findMin(), findMax())
              },
            )(),
          }),
        // Save cursor position to be able to use it as the initial point for drag
        timestamp => {
          cursorTime = timestamp
        },
        // Start dragging if enough shift
        timestamp => {
          if (
            isPreDragging &&
            Math.abs(dragInitialDate.diff(moment(timestamp))) >
              currentScale / 100
          ) {
            setState({
              isDragging: true,
              isPreDragging: false,
              preDragTimeOut: clearTimeout(preDragTimeOut),
            })
          }
        },
        // Dragging logic
        timestamp => {
          if (isDragging) {
            const limitIfInPlan = date =>
              plan
                ? limitDate(
                    date,
                    moment(get(plan, `data.startDateTime`)),
                    moment(get(plan, `data.endDateTime`)),
                  )
                : date

            const normalize = pipe(
              date =>
                snapToGrid(
                  date,
                  activeEvent.eventTypeAllowOnlyDate
                    ? getGreaterSnapUnit(
                        getSnapUnit(currentScale),
                        snapUnits.day,
                      )
                    : getSnapUnit(currentScale),
                ),
              limitIfInPlan,
            )

            const inLeftHalf = dragOffset < dragEventDuration / 2
            const inRightHalf = dragOffset >= dragEventDuration / 2

            if (inLeftHalf) {
              const mainPoint = normalize(moment(timestamp - dragOffset))
              const otherPoint = limitIfInPlan(
                mainPoint.clone().add(dragEventDuration, `ms`),
              )

              const doOtherPointFits =
                otherPoint.diff(mainPoint) === dragEventDuration

              setState({
                startDate: doOtherPointFits
                  ? mainPoint
                  : otherPoint.clone().subtract(dragEventDuration, `ms`),
                endDate: otherPoint,
              })
            } else if (inRightHalf) {
              const mainPoint = normalize(
                moment(timestamp - dragOffset + dragEventDuration),
              )
              const otherPoint = limitIfInPlan(
                mainPoint.clone().subtract(dragEventDuration, `ms`),
              )

              const doOtherPointFits =
                mainPoint.diff(otherPoint) === dragEventDuration

              setState({
                startDate: otherPoint,
                endDate: doOtherPointFits
                  ? mainPoint
                  : otherPoint.clone().add(dragEventDuration, `ms`),
              })
            }
          }
        },
      ]),
  }),
  // Generate the props to pass to original component
  withPropsOnChange(
    makeCompareFunction([
      `outerProps.items`,
      `activeEventId`,
      `isGrabbing`,
      `isDragging`,
      `startDate`,
      `endDate`,
    ]),
    ({
      outerProps: { items, canEditEvent },
      handleLeftHandlerMouseDown,
      handleRightHandlerMouseDown,
      handleItemMouseDown,
      activeEventId,
      startDate,
      endDate,
      isGrabbing,
      isDragging,
      isPreDragging,
    }) => ({
      // Visualize resizing/dragging
      style: (() => {
        if (isGrabbing) return { cursor: `ew-resize` }
        if (isDragging || isPreDragging) return { cursor: `grabbing` }
        return null
      })(),
      // Transform the items
      items: items.map(item =>
        canEditEvent(item)
          ? pipe(
              // Add handlers to handles
              ({
                originId,
                original,
                start_time: startDate,
                end_time: endDate,
                status,
                restrictions,
                ...rest
              }) => {
                const canEditStart = get(restrictions, `startDateTime`)
                const canEditEnd = get(restrictions, `endDateTime`)
                const lockedEvent =
                  get(item, `original.locked`) ||
                  get(item, `original.planInfo.locked`)
                return {
                  originId,
                  original,
                  start_time: startDate,
                  end_time: endDate,
                  onLeftHandlerMouseDown: () =>
                    !(canEditStart || lockedEvent)
                      ? handleLeftHandlerMouseDown(
                          originId,
                          original,
                          moment(startDate),
                          moment(endDate),
                        )
                      : noop,
                  onRightHandlerMouseDown: () =>
                    !(canEditEnd || lockedEvent)
                      ? handleRightHandlerMouseDown(
                          originId,
                          original,
                          moment(startDate),
                          moment(endDate),
                        )
                      : noop,
                  onBodyMouseDown: () =>
                    !(canEditStart || canEditEnd || lockedEvent)
                      ? handleItemMouseDown(
                          originId,
                          original,
                          moment(startDate),
                          moment(endDate),
                        )
                      : noop,
                  status,
                  restrictions,
                  ...rest,
                }
              },
              // Adding styles to handlers
              item => {
                const canEditStart = get(item, `restrictions.startDateTime`)
                const canEditEnd = get(item, `restrictions.endDateTime`)
                const lockedEvent =
                  get(item, `original.locked`) ||
                  get(item, `original.planInfo.locked`)
                return {
                  ...item,
                  leftHandlerStyle: {
                    cursor: !(canEditStart || lockedEvent)
                      ? `w-resize`
                      : `not-allowed`,
                  },
                  rightHandlerStyle: {
                    cursor: !(canEditEnd || lockedEvent)
                      ? `e-resize`
                      : `not-allowed`,
                  },
                }
              },
              // Change time points of currently grabbed item
              item => {
                if (item.originId === activeEventId) {
                  return {
                    ...item,
                    start_time: startDate,
                    end_time: endDate,
                  }
                }
                return item
              },
              // Style corrections on any interacting
              isDragging || isGrabbing
                ? item => ({
                    ...item,
                    timeLabel:
                      item.originId === activeEventId
                        ? getTimeLabel(startDate, endDate)
                        : null,
                    style: {
                      ...item.style,
                      pointerEvents: `none`,
                      ...(activeEventId === item.originId
                        ? {
                            boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.12)`,
                            zIndex: 31,
                            lineHeight: `normal`,
                          }
                        : {}),
                    },
                  })
                : identity,
              // Style corrections to dragging element
              isDragging
                ? item =>
                    activeEventId === item.originId
                      ? {
                          ...item,
                          style: {
                            ...item.style,
                            cursor: `grabbing`,
                          },
                        }
                      : item
                : identity,
            )(item)
          : item,
      ),
    }),
  ),

  BaseComponent => props => {
    const {
      outerProps,
      handleCanvasMouseMove,
      handleMouseUp,
      handleEscapePress,
      style,
      items,
    } = props

    return (
      <>
        <GlobalEscapeHandle onEscapePress={handleEscapePress} />
        <GlobalMouseHandle onMouseUp={handleMouseUp} />
        <BaseComponent
          {...outerProps}
          onCanvasMouseMove={handleCanvasMouseMove}
          {...(style ? { style } : {})}
          items={items}
        />
      </>
    )
  },
)
